/**
 * Meta Pixel (web only): init, PageView, CompleteRegistration, Subscribe.
 * Deduplicate with CAPI using the same event_id.
 */

declare global {
  interface Window {
    fbq?: (
      action: string,
      eventName: string,
      params?: Record<string, unknown>,
    ) => void;
  }
}

const PIXEL_ID = process.env.EXPO_PUBLIC_META_PIXEL_ID || '';

let initialized = false;

function isWeb(): boolean {
  return typeof window !== 'undefined';
}

function loadPixelScript(): void {
  if (!PIXEL_ID || initialized) return;
  const script = document.createElement('script');
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${PIXEL_ID}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);
  initialized = true;
}

/**
 * Initialize Meta Pixel (call once on app load, web). Fires initial PageView.
 */
export function initMetaPixel(): void {
  if (!isWeb() || !PIXEL_ID) return;
  loadPixelScript();
}

/**
 * Track PageView (optional; init already fires one).
 */
export function trackPageView(): void {
  if (!isWeb() || !PIXEL_ID || !window.fbq) return;
  window.fbq('track', 'PageView');
}

/**
 * Track CompleteRegistration (provider sign-up). Use same event_id as CAPI for deduplication.
 */
export function trackCompleteRegistration(eventId: string): void {
  if (!isWeb() || !PIXEL_ID || !window.fbq) return;
  window.fbq('track', 'CompleteRegistration', {}, { eventID: eventId });
}

/**
 * Track InitiateCheckout - chegou no checkout (para audiência de retargeting).
 */
export function trackInitiateCheckout(options?: { value?: number; currency?: string; contentIds?: string[] }): void {
  if (!isWeb() || !PIXEL_ID || !window.fbq) return;
  const params: Record<string, unknown> = {};
  if (options?.value != null) params.value = options.value;
  if (options?.currency) params.currency = options.currency;
  if (options?.contentIds?.length) params.content_ids = options.contentIds;
  window.fbq('track', 'InitiateCheckout', params);
}

/**
 * Track Purchase - compra concluída (para audiência de compradores / Lookalike).
 */
export function trackPurchase(
  options: { value: number; currency: string; contentIds?: string[]; orderId?: string },
): void {
  if (!isWeb() || !PIXEL_ID || !window.fbq) return;
  const params: Record<string, unknown> = {
    value: options.value,
    currency: options.currency,
  };
  if (options.contentIds?.length) params.content_ids = options.contentIds;
  if (options.orderId) params.order_id = options.orderId;
  window.fbq('track', 'Purchase', params);
}

/**
 * Track Subscribe (subscription confirmed). Use same event_id as CAPI for deduplication.
 */
export function trackSubscribe(
  eventId: string,
  options?: { value?: number; currency?: string },
): void {
  if (!isWeb() || !PIXEL_ID || !window.fbq) return;
  const customData: Record<string, unknown> = { ...options };
  window.fbq('track', 'Subscribe', customData, { eventID: eventId });
}

export function hasMetaPixel(): boolean {
  return !!PIXEL_ID && isWeb();
}
